import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useCrypto } from './crypto';
import { api } from '../api/client';
import { encrypt, decrypt } from '@mysecureroom/crypto';

export interface LifeArea {
  id: string;
  name: string;
  icon: string;
  color: string;
  sort_order: number;
  is_default: number;
  created_at: string;
  latest_rating: number | null;
  rating_notes: string | null;
  last_rated_at: string | null;
}

export interface LifeAreaRating {
  id: string;
  life_area_id: string;
  rating: number; // 0-10
  notes: string | null;
  rated_at: string;
}

export const DEFAULT_LIFE_AREAS = [
  { name: 'Career & Work', icon: '💼', color: '#3B82F6', sort_order: 0 },
  { name: 'Health & Fitness', icon: '💪', color: '#10B981', sort_order: 1 },
  { name: 'Relationships & Love', icon: '❤️', color: '#EF4444', sort_order: 2 },
  { name: 'Personal Growth', icon: '🌱', color: '#8B5CF6', sort_order: 3 },
  { name: 'Finance & Wealth', icon: '💰', color: '#F59E0B', sort_order: 4 },
  { name: 'Fun & Recreation', icon: '🎉', color: '#EC4899', sort_order: 5 },
  { name: 'Physical Environment', icon: '🏡', color: '#14B8A6', sort_order: 6 },
  { name: 'Contribution & Purpose', icon: '🎯', color: '#6366F1', sort_order: 7 },
];

interface LifeAreasState {
  areas: LifeArea[];
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

interface LifeAreasContextValue extends LifeAreasState {
  initialize: () => Promise<void>;
  rateArea: (areaId: string, rating: number, notes?: string) => Promise<void>;
  rateAllAreas: (ratings: Array<{ areaId: string; rating: number }>) => Promise<void>;
  createCustomArea: (name: string, icon: string, color: string) => Promise<void>;
  deleteCustomArea: (areaId: string) => Promise<void>;
  clear: () => void;
}

const LifeAreasContext = createContext<LifeAreasContextValue | null>(null);

export function LifeAreasProvider({ children }: { children: ReactNode }) {
  const crypto = useCrypto();
  const [state, setState] = useState<LifeAreasState>({
    areas: [],
    loading: false,
    error: null,
    initialized: false,
  });

  const encryptText = async (text: string): Promise<string> => {
    if (!crypto.encryptionKey) throw new Error('Encryption key not available');
    return encrypt(crypto.encryptionKey, text);
  };

  const decryptText = async (encrypted: string): Promise<string> => {
    if (!crypto.encryptionKey) throw new Error('Encryption key not available');
    return decrypt(crypto.encryptionKey, encrypted);
  };

  const initialize = async () => {
    // Don't initialize if encryption key isn't ready yet
    if (!crypto.encryptionKey) {
      console.log('Waiting for encryption key...');
      return;
    }

    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const result = await api.get<{ areas: LifeArea[] }>('/lifeareas');

      console.log('Life areas loaded from API:', result.areas);

      // Decrypt custom area names (default areas not encrypted) and rating notes
      const decryptedAreas = await Promise.all(
        result.areas.map(async (a) => {
          let decryptedNotes = null;

          // Only try to decrypt if rating_notes exists and isn't empty
          if (a.rating_notes && a.rating_notes.trim()) {
            try {
              console.log(`Decrypting notes for area ${a.name}:`, a.rating_notes.substring(0, 20) + '...');
              decryptedNotes = await decryptText(a.rating_notes);
              console.log(`Successfully decrypted notes for ${a.name}:`, decryptedNotes);
            } catch (err) {
              console.error(`Failed to decrypt notes for area ${a.name}:`, err);
              // Notes are encrypted but can't decrypt - set to empty to avoid showing hash
              decryptedNotes = '';
            }
          }

          return {
            ...a,
            name: a.is_default ? a.name : await decryptText(a.name),
            rating_notes: decryptedNotes,
          };
        })
      );

      console.log('Decrypted life areas:', decryptedAreas);

      setState({
        areas: decryptedAreas.sort((a, b) => a.sort_order - b.sort_order),
        loading: false,
        error: null,
        initialized: true,
      });
    } catch (error) {
      console.error('Failed to load life areas:', error);
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load life areas',
        initialized: true, // Set initialized even on error
      }));
    }
  };

  const rateArea = async (areaId: string, rating: number, notes?: string) => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const encryptedNotes = notes ? await encryptText(notes) : undefined;
      await api.post(`/lifeareas/${areaId}/rate`, {
        rating,
        notes: encryptedNotes,
      });

      setState(s => ({
        ...s,
        areas: s.areas.map(a => a.id === areaId ? {
          ...a,
          latest_rating: rating,
          rating_notes: notes || null
        } : a),
        loading: false,
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to rate area',
      }));
    }
  };

  const rateAllAreas = async (ratings: Array<{ areaId: string; rating: number }>) => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      await api.post('/lifeareas/rate-all', { ratings });

      const updatedAreas = state.areas.map(a => {
        const rating = ratings.find(r => r.areaId === a.id);
        return rating ? { ...a, latest_rating: rating.rating } : a;
      });

      setState(s => ({ ...s, areas: updatedAreas, loading: false }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to rate areas',
      }));
    }
  };

  const createCustomArea = async (name: string, icon: string, color: string) => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const encryptedName = await encryptText(name);
      const result = await api.post<{ area: LifeArea }>('/lifeareas', {
        name: encryptedName,
        icon,
        color,
      });

      const decryptedArea = {
        ...result.area,
        name: await decryptText(result.area.name),
      };

      setState(s => ({
        ...s,
        areas: [...s.areas, decryptedArea].sort((a, b) => a.sort_order - b.sort_order),
        loading: false,
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to create area',
      }));
    }
  };

  const deleteCustomArea = async (areaId: string) => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      await api.delete(`/lifeareas/${areaId}`);
      setState(s => ({
        ...s,
        areas: s.areas.filter(a => a.id !== areaId),
        loading: false,
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to delete area',
      }));
    }
  };

  const clear = () => {
    setState({
      areas: [],
      loading: false,
      error: null,
      initialized: false,
    });
  };

  const value: LifeAreasContextValue = {
    ...state,
    initialize,
    rateArea,
    rateAllAreas,
    createCustomArea,
    deleteCustomArea,
    clear,
  };

  return <LifeAreasContext.Provider value={value}>{children}</LifeAreasContext.Provider>;
}

export function useLifeAreas() {
  const context = useContext(LifeAreasContext);
  if (!context) {
    throw new Error('useLifeAreas must be used within LifeAreasProvider');
  }
  return context;
}
